import { useEffect, useRef, useState } from "react";
import {
  Modal,
  Box,
  Button,
  Stack,
  Typography,
  Autocomplete,
  TextField,
  InputAdornment,
  AutocompleteChangeReason,
  AutocompleteChangeDetails,
  IconButton,
} from "@mui/material";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { OlaMapsStore } from "../../../../../store/main/OlaMapsStore";
import { FetchState } from "../../../../../domain/models/fetch-state";
import AddConfirmHQLocationDashBoardModal from "./AddConfirmHQLocationDashBoardModal";
import DeleteConfirmHQLocationDashBoardModal from "./DeleteConfirmHQLocationDashBoardModal";
import { UserHQLocationDashBoardStore } from "../../../../../store/main/UserHQLocationDashBoardStore";
import Swal from "sweetalert2";

type LocationMetadata = {
  status: string; 
  results: LocationResult[]; 
};

type LocationResult = {
  types: string[]; 
  placeID: string; 
  geometry: Geometry;
  plusCode: PlusCode; 
  formattedAddress: string; 
  addressComponents: AddressComponent[]; 
};

type Geometry = {
  location: Coordinates; 
  viewport: Viewport; 
  locationType: string; 
};

type Coordinates = {
  lat: number;
  lng: number; 
};

type Viewport = {
  northeast: Coordinates; 
  southwest: Coordinates;
};

type PlusCode = {
  globalCode: string; 
  compoundCode: string; 
};

type AddressComponent = {
  types: string[]; 
  longName: string; 
  shortName: string; 
};

type Prediction = {
  reference?: string;
  types?: string[];
  matched_substrings?: MatchedSubstring[];
  terms?: Term[];
  structured_formatting?: StructuredFormatting;
  description: string;
  geometry?: GeometryPrediction;
  place_id: string;
  layer?: string[];
};

type MatchedSubstring = {
  offset: number;
  length: number;
};

type Term = {
  offset: number;
  value: string;
};

type StructuredFormatting = {
  main_text_matched_substrings: MatchedSubstring[];
  secondary_text_matched_substrings: MatchedSubstring[];
  secondary_text: string;
  main_text: string;
};

type GeometryPrediction = {
  location: {
    lng: number;
    lat: number;
  };
};

type CreateUpdateHQLocationDashBoardModalProps = {
  lat: number | null;
  long: number | null;
  open: boolean;
  setOpen: (value: boolean) => void;
  locationMetaData: LocationMetadata | null;
};

const CreateUpdateHQLocationDashBoardModal = ({
  lat,
  long,
  open,
  setOpen,
  locationMetaData,
}: CreateUpdateHQLocationDashBoardModalProps) => {
  const [inputValue, setInputValue] = useState<string>("");
  const [options, setOptions] = useState<Prediction[]>([]);
  const [marker, setMarker] = useState<
    { lat: number; lng: number; place_id: string }[]
  >([]);
  const [selectedHQLocation, setSelectedHQLocation] = useState<{
    formatted_address: string;
    place_id: string;
    lat: number;
    lng: number;
  } | null>(null);
  const [confirmAddHQLocationModalOpen, setConfirmAddHQLocationModalOpen] =
    useState<boolean>(false);
  const [
    confirmDeleteHQLocationModalOpen,
    setConfirmDeleteHQLocationModalOpen,
  ] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [locationMetadata, setLocationMetadata] =
    useState<LocationMetadata | null>(null);
  const [
    addUpdateLocationLoading,
    setAddUpdateLocationLoading,
  ] = useState<boolean>(false);

  const defaultCenter = { lat: lat ?? 21.7679, lng: long ?? 78.8718 };
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const mapRef = useRef<google.maps.Map | null>(null);

const onMapLoad = (map: google.maps.Map) => {
  mapRef.current = map;
};

useEffect(() => {
  if (marker.length > 0 && mapRef.current) {
    const { lat, lng } = marker[0];

    mapRef.current.panTo({ lat, lng });
    mapRef.current.setZoom(14); // adjust as needed
  }
}, [marker]);


  const {
    FetchOlaMapsLocationByLatLong,
    FetchOlMapsLocationByLatLongState,
    fetchOlaMapsLocationByLatLong,
    FetchOlaMapsAutoCompleteLocation,
    FetchOlMapsAutoCompleteLocationState,
    fetchOlaMapsAutoCompleteLocation,
    FetchOlaMapsLocationByPlaceID,
    FetchOlaMapsLocationByPlaceIDState,
    fetchOlaMapsLocationByPlaceID,
  } = OlaMapsStore();

  const {
    createUpdateUserHQLocationDashBoard,
    CreateUpdateUserHQLocationDashBoardState,
  } = UserHQLocationDashBoardStore();

  const handleInputChange = (
    event: React.ChangeEvent<{}>,
    newValue: string,
    reason: string
  ) => {
    if (reason === "reset") return;
    setInputValue(newValue);
    if (newValue.trim()) {
      fetchOlaMapsAutoCompleteLocation({ input: newValue });
    }
  };

  useEffect(() => {
    if (FetchOlMapsAutoCompleteLocationState === FetchState.SUCCESS) {
      if (
        FetchOlaMapsAutoCompleteLocation &&
        FetchOlaMapsAutoCompleteLocation.location_data
      ) {
        const fetchedOptions =
          FetchOlaMapsAutoCompleteLocation.location_data.predictions.map(
            (prediction) => ({
              description: prediction.description,
              place_id: prediction.place_id,
            })
          );
        setOptions(fetchedOptions);
      }
    }
  }, [FetchOlaMapsAutoCompleteLocation, FetchOlMapsAutoCompleteLocationState]);

    useEffect(() => {
    if (lat && long && locationMetaData && locationMetaData.results.length > 0) {
      const previousHQResult = locationMetaData.results; 
     

      const extractedHQData = previousHQResult.map((result: any) => ({
        place_id: result.place_id,
        formatted_address: result.formatted_address,
      }));

      const previousHQMarker = {
        lat: lat,
        lng: long,
        place_id: extractedHQData[0].place_id || "", 
      };

      setMarker([previousHQMarker]);

      const previousHQLocation = {
        place_id: extractedHQData[0].place_id || "",
        formatted_address: extractedHQData[0].formatted_address || "",
        lat: lat,
        lng: long,
      };

      setSelectedHQLocation(previousHQLocation);
      
      
    }
  }, [lat, long, locationMetaData]);

  const handleHQLocationChange = async (
    event: React.SyntheticEvent,
    value: any | null
  ) => {
    if (selectedHQLocation) {
      setErrorMessage("Only one HQ location can be added.");
      return;
    }

    if (value && typeof value !== "string") {
      try {
        let placeIdApiResponse = await fetchOlaMapsLocationByPlaceID({
          place_id: value.place_id,
        });

        if (
          placeIdApiResponse.success &&
          placeIdApiResponse.location_data?.result
        ) {
          let newLocationData = placeIdApiResponse.location_data.result;
          let newLocationLatLng = newLocationData.geometry.location;
  
          let newMarker = {
            lat: newLocationLatLng.lat,
            lng: newLocationLatLng.lng,
            place_id: newLocationData.place_id,
          };
         
          setMarker([newMarker]);

          let newHQLocation = {
            place_id: newLocationData.place_id,
            formatted_address: newLocationData.formatted_address,
            lat: newLocationLatLng.lat ?? 0,
            lng: newLocationLatLng.lng ?? 0,
          };

          setSelectedHQLocation(newHQLocation);
          setMapCenter({
            lat: newLocationLatLng.lat,
            lng: newLocationLatLng.lng,
          });
          setConfirmAddHQLocationModalOpen(true); 
          setErrorMessage(""); 

          let params = {
            latitude: newLocationLatLng.lat,
            longitude: newLocationLatLng.lng,
          };

          let reverseGeocodeApiResponse = await fetchOlaMapsLocationByLatLong(
            params
          );
          if (reverseGeocodeApiResponse.success) {
            setLocationMetadata(reverseGeocodeApiResponse.location_data);
          }
        } else {
          console.error("Invalid API response format:", placeIdApiResponse);
        }
      } catch (error) {
        console.error("Error fetching location details:", error);
      }
    }
  };


  const handleMapClickHQLocation = async (event: google.maps.MapMouseEvent) => {
    if (!event.latLng) return;

    const clickedLat = event.latLng.lat();
    const clickedLng = event.latLng.lng();

    if (selectedHQLocation) {
      setErrorMessage("Only one HQ location can be added.");
      return;
    }

    setMapCenter({ lat: clickedLat, lng: clickedLng });

    try {
      const clickedLatLng = { latitude: clickedLat, longitude: clickedLng };

      let reverseGeocodeApiResponse = await fetchOlaMapsLocationByLatLong(
        clickedLatLng
      );
      if (
        reverseGeocodeApiResponse.success &&
        reverseGeocodeApiResponse.location_data &&
        reverseGeocodeApiResponse.location_data.results
      ) {
        let locationData = reverseGeocodeApiResponse.location_data.results[0];
        let placeId = locationData.place_id || "";

        let placeIdApiResponse = await fetchOlaMapsLocationByPlaceID({
          place_id: placeId,
        });

        if (
          placeIdApiResponse.success &&
          placeIdApiResponse.location_data?.result
        ) {
          let newLocationData = placeIdApiResponse.location_data.result;
          let newLocationLatLng = newLocationData.geometry.location;

          let newMarker = {
            lat: newLocationLatLng.lat,
            lng: newLocationLatLng.lng,
            place_id: newLocationData.place_id,
          };

          setMarker([newMarker]);

          let newHQLocation = {
            place_id: newLocationData.place_id,
            formatted_address: newLocationData.formatted_address,
            lat: newLocationLatLng.lat,
            lng: newLocationLatLng.lng,
          };

          setSelectedHQLocation(newHQLocation);

          setMapCenter({
            lat: newLocationLatLng.lat,
            lng: newLocationLatLng.lng,
          });

          setTimeout(() => {
            setConfirmAddHQLocationModalOpen(true);
          }, 100);

          setErrorMessage("");
          setLocationMetadata(reverseGeocodeApiResponse.location_data);
        } else {
          console.error(
            " Invalid Place ID API Response:",
            placeIdApiResponse
          );
        }
      } else {
        console.error(
          "Invalid Reverse Geocode API Response:",
          reverseGeocodeApiResponse
        );
      }
    } catch (error) {
      console.error(" Error fetching location details:", error);
    }
  };


  const handleMapDrag = () => {
    setErrorMessage("");
  };

  const handleConfirmAddHQLocation = async (headquarter_id: number, brick_id: number) => {
    if (addUpdateLocationLoading) return
      if (!selectedHQLocation) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "At least one location is required!",
        });
        return;
      }
     setMarker([
       {
         lat: selectedHQLocation.lat,
         lng: selectedHQLocation.lng,
         place_id: selectedHQLocation.place_id,
       },
     ]);
     setInputValue(""); 
     setConfirmAddHQLocationModalOpen(false);

     setAddUpdateLocationLoading(true);

     try {
       let params = {
         latitude: selectedHQLocation.lat,
         longitude: selectedHQLocation.lng,
         location_metadata: locationMetadata,
         headquarter_id,
         brick_id,
       };

       let createUpdateHQResult = await createUpdateUserHQLocationDashBoard(
         params
       );

       Swal.fire({
         icon: "success",
         title: "Success",
         timer: 1000, 
         text: createUpdateHQResult.message,
         customClass: {
           container: "swal2-style",
         },
         confirmButtonColor: "#3085d6",
         showConfirmButton: false, 
       }).then(() => {
         window.parent.postMessage({ type: "MODAL_CLOSED_HQ_LOCATION" }, "*");
       });

      //  setSelectedHQLocation(null);
      //  setOpen(false); 
     } catch (error) {
       console.error("Error creating/updating HQ location:", error);
       Swal.fire({
         icon: "error",
         title: "Error",
         customClass: {
           container: "swal2-style",
         },
         confirmButtonColor: "#3085d6",
       });
     } finally {
       setAddUpdateLocationLoading(false);
     }
  };

  const handleCloseConfirmModal = () => {
    setConfirmAddHQLocationModalOpen(false);
    setSelectedHQLocation(null); 
    setMarker([]); 
    setInputValue(""); 
  };

  const handleConfirmDeleteLocation = () => {
    setConfirmDeleteHQLocationModalOpen(false); 
    setSelectedHQLocation(null); 
    setMarker([]);
    setInputValue(""); 
    setErrorMessage(""); 
  };

  const handleClose = () => setOpen(false);

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        position="absolute"
        top="50%"
        left="50%"
        bgcolor="background.paper"
        borderRadius="22px"
        sx={{
          transform: "translate(-50%, -50%)",
          display: "flex",
          overflow: "hidden",
        }}
        width="100%"
        p={3}
      >
        <Box display="flex" flexDirection="row" width="100%" height="100%">
          <Box flex="1">
            <Box
              display="flex"
              position={"absolute"}
              top={20}
              zIndex={1}
              bgcolor={"white"}
              marginTop={8}
              marginLeft={2}
              width={"30%"}
            >
              <Box flex="1" marginRight={2}>
                <Typography
                  fontWeight={500}
                  variant="h6"
                  textAlign="left"
                  marginTop={1}
                  marginLeft={3}
                >
                  Search Your HQ Location
                </Typography>

                <Autocomplete
                  sx={{ marginBottom: "20px", marginLeft: "20px" }}
                  disablePortal
                  freeSolo
                  disabled={!!selectedHQLocation} 
                  inputValue={inputValue}
                  onInputChange={handleInputChange}
                  options={options}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option.description
                  }
                  onChange={handleHQLocationChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      placeholder="Search HQ Location on Map"
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      error={!!errorMessage} 
                      helperText={errorMessage} 
                    />
                  )}
                />
                <Box
                  flex="1"
                  pl={2}
                  sx={{ overflowY: "auto", maxHeight: "40vh" }}
                >
                  <Typography
                    fontWeight={700}
                    variant="h6"
                    textAlign="left"
                    marginBottom={2}
                  >
                    Add 1 HQ Location
                  </Typography>
                  <Stack spacing={1}>
                    {selectedHQLocation && (
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        bgcolor="lightblue"
                        p={1}
                        borderRadius={1}
                      >
                        <Typography fontWeight="bold">
                          {selectedHQLocation.formatted_address}
                        </Typography>
                        {/* <IconButton
                          onClick={() => {
                            setSelectedHQLocation(selectedHQLocation);
                            setConfirmDeleteHQLocationModalOpen(true);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton> */}
                      </Box>
                    )}
                  </Stack>
                </Box>
                
                <Box display="flex" justifyContent="center" width="100%" mt={2}>
                </Box>
                <AddConfirmHQLocationDashBoardModal
                  open={confirmAddHQLocationModalOpen}
                  location={selectedHQLocation}
                  onConfirm={(hqId, brickId) => handleConfirmAddHQLocation(hqId, brickId)}
                  onCancel={handleCloseConfirmModal}
                />
                <DeleteConfirmHQLocationDashBoardModal
                  open={confirmDeleteHQLocationModalOpen}
                  location={selectedHQLocation}
                  handleDeleteConfirm={handleConfirmDeleteLocation}
                  handleDeleteClose={() =>
                    setConfirmDeleteHQLocationModalOpen(false)
                  }
                />
              </Box>
            </Box>
            <LoadScript googleMapsApiKey="AIzaSyCv-70Q4VJ5VZYPzQs06SuUiA-QX0195Nw">
              <GoogleMap
                mapContainerStyle={{ height: "100vh", width: "100%" }}
                zoom={selectedHQLocation ? 14 : 4}
                center={mapCenter} 
                  onLoad={onMapLoad}
                onClick={handleMapClickHQLocation}
                onDragStart={handleMapDrag}
              >
                {marker.map((marker, index) => (
                  <Marker
                    key={index}
                    position={{ lat: marker.lat, lng: marker.lng }}
                  />
                ))}
              </GoogleMap>
            </LoadScript>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default CreateUpdateHQLocationDashBoardModal;
